import React, { useCallback, useMemo, useState } from 'react';
import { useModalActions } from '../../Modals/modalActions';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { ContractTileUI } from '@cube3/ui/src/Prefabs/contracts/ContractTileUI';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { Contract } from '@cube3/common/model/schema/resources/contract';

import { generatePath, useHistory } from 'react-router';
import {
  getAssetUrl,
  urlStructureProject
} from '../../../routing/routingPaths';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { useUiSettings } from '../../ContentTree/hooks/useUiSettings';
import { ContractView } from '../pages/ContractView';
import { MobileSubmenuSlider } from '@cube3/cubicle/src/components/Surface/MobileSubmenuSlider';
import { isMobile } from 'react-device-detect';
import { AttachedContractsCommandBar } from './command-bars/AttachedContractsCommandBar';
import { ResourceIdentifier } from '@cube3/common/model/resource-types';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';

interface Props {
  contract: Contract;
  variant?: 'slim' | 'regular';
  expiresSoonOffsetDay?: number;
  parentResource?: ResourceIdentifier;
}

export const ContractTile = React.memo(
  ({
    contract,
    variant = 'regular',
    expiresSoonOffsetDay,
    parentResource
  }: Props) => {
    const modalActions = useModalActions();

    const [openSelected, setOpenSelected] = useState(false);
    const contractType = useResource__ALPHA({
      resourceId: contract.relationships.contract_type?.id,
      resourceType: contract.relationships.contract_type?.type
    });

    const attachedAssets = useResourceList__ALPHA({
      resourceId: variant === 'regular' && contract.id,
      resourceType: contract.type,
      edgeType: 'asset',
      edgeLabel: 'assets'
    });

    const attachedProjects = useResourceList__ALPHA({
      resourceId: variant === 'regular' && contract.id,
      resourceType: contract.type,
      edgeType: 'project',
      edgeLabel: 'projects'
    });

    const history = useHistory();
    const [workspaceId] = useCurrentWorkspace();

    const attachments = useMemo(() => {
      if (
        attachedAssets.status !== statuses.SUCCESS ||
        attachedProjects.status !== statuses.SUCCESS
      ) {
        return undefined;
      }

      const all = [...attachedProjects.resources, ...attachedAssets.resources];

      return all.map((r) => ({
        name: r.display_name,
        type: r.type,
        onClick: (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          const path =
            r.type === 'project'
              ? generatePath(urlStructureProject, {
                  workspaceId,
                  projectId: r.id
                })
              : getAssetUrl({
                  assetId: r.id,
                  workspaceId
                });
          history.push(path, { from: 'previous' });
        }
      }));
    }, [
      attachedAssets.resources,
      attachedProjects.resources,
      attachedAssets.status,
      attachedProjects.status,
      history,
      workspaceId
    ]);

    const manageContract = useCallback(
      () =>
        modalActions.openModal('manage_contract', { contractId: contract.id }),
      [contract, modalActions]
    );

    const { selectedContract: selection, selectContract: setSelection } =
      useUiSettings();
    const selectContract = useCallback(() => {
      if (!selection || selection.id !== contract.id) {
        setSelection(contract);
      }
    }, [setSelection, selection, contract]);

    const handleClick = useCallback(() => {
      if (isMobile) {
        setOpenSelected(true);
      } else {
        variant !== 'slim' ? manageContract() : selectContract();
      }
    }, [isMobile, variant, manageContract, selectContract, contract]);

    return (
      <>
        <ContractTileUI
          expiresSoonOffsetDay={expiresSoonOffsetDay}
          item={{
            ...contract,
            __contractType: contractType.resource?.display_name
          }}
          attachments={attachments}
          selected={
            selection && variant === 'slim' && selection.id === contract.id // Prevent selecting a contract in the contract overview
          }
          onClick={handleClick}
          showBorder={variant !== 'slim'}
          showAttachments={variant !== 'slim'}
          showDate={true}
        />
        {openSelected && (
          <MobileSubmenuSlider onClose={() => setOpenSelected(false)}>
            <AttachedContractsCommandBar
              contract={contract as ResourceIdentifier}
              parentResource={parentResource}
              mobileSliderLevel="second"
            />
            <Divider />
            <ContractView contractId={contract.id} />
          </MobileSubmenuSlider>
        )}
      </>
    );
  }
);
